'use client'

import { useTranslations } from 'next-intl'
import { ReactNode } from 'react'
import useSWRInfinite from 'swr/infinite'

import Swimlane, { SwimlaneSkeleton } from '@/app/[locale]/(worlds)/components/Swimlane'
import CreatorAvatar, { CreatorAvatarSkeleton } from '@/components/CreatorAvatar'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useDraggingScrollMotion } from '@/hooks/useDraggingScrollMotion'
import { useLoadMore } from '@/hooks/useLoadMore'
import { hasVerifiedCreatorBadge } from '@/lib/cms'
import { cn } from '@/lib/utils'
import type {
  AuthenticatedCommunityIdentity,
  PublicFollowingWithIcon,
  PublicProfileCommunityIdentity,
} from '@/services/community'
import {
  getAuthenticatedFollowingsWithIcon,
  getPublicFollowingsWithIcon,
} from '@/services/community'

const PAGE_SIZE = 10

type FollowingCreator = PublicFollowingWithIcon

type FollowingCreatorsPage = {
  data: FollowingCreator[]
  total: number
  nextFrom: number | null
}

type FollowingCreatorsKeyLoader = (
  pageIndex: number,
  previousPage: FollowingCreatorsPage | null,
) => readonly [string, number] | null

type FollowingCreatorsProps = {
  title?: string
  titleAction?: ReactNode
  emptyState?: ReactNode
  sectionClassName?: string
  scrollContainerClassName?: string
  contentClassName?: string
  publicIdentity?: PublicProfileCommunityIdentity
}

export default function FollowingCreators({
  title,
  titleAction,
  emptyState,
  sectionClassName,
  scrollContainerClassName,
  contentClassName,
  publicIdentity,
}: FollowingCreatorsProps) {
  const t = useTranslations()
  const { store } = useConfigsContext()
  const htcToken = store?.credentials?.htcToken
  const preferredUsername = store?.profile?.userId

  const authenticatedIdentity: AuthenticatedCommunityIdentity | undefined =
    htcToken && preferredUsername ? { htcToken, preferredUsername } : undefined

  const swrKey = publicIdentity?.preferredUsername
    ? `publicFollowingCreators:${publicIdentity.preferredUsername}`
    : authenticatedIdentity
      ? 'followingCreators'
      : null

  const { data, isLoading, isValidating, size, setSize } = useSWRInfinite<
    FollowingCreatorsPage,
    Error,
    FollowingCreatorsKeyLoader
  >(
    (pageIndex, previousPage) => {
      if (!swrKey || previousPage?.nextFrom === null) return null
      return [swrKey, previousPage?.nextFrom ?? pageIndex * PAGE_SIZE] as const
    },
    async ([, from]) => {
      if (publicIdentity) {
        const result = await getPublicFollowingsWithIcon(publicIdentity, from, PAGE_SIZE)
        const nextFrom = result.from + result.size
        return {
          data: result.data,
          total: result.total,
          nextFrom: nextFrom < result.total ? nextFrom : null,
        }
      }

      const result = await getAuthenticatedFollowingsWithIcon(
        authenticatedIdentity!,
        from,
        PAGE_SIZE,
      )

      const nextFrom = result.from + result.size

      return {
        data: result.data,
        total: result.total,
        nextFrom: nextFrom < result.total ? nextFrom : null,
      }
    },
    { revalidateFirstPage: false },
  )

  const creators = (data?.flatMap((page) => page.data) ?? []).filter((c) => c.preferredUsername)
  const lastPage = data?.[data.length - 1]

  const enableLoadMore = lastPage?.nextFrom != null

  const loadMoreRef = useLoadMore({
    enabled: enableLoadMore,
    isLoading,
    isValidating,
    setSize,
    size,
  })

  if (!swrKey) return null

  if (isLoading) {
    return (
      <div
        id="following-creators"
        data-section="profile-following-creators"
        className="scroll-mt-40"
      >
        <SwimlaneSkeleton
          showTitleAction
          className={cn('gap-4 md:gap-2 lg:gap-[10px]', sectionClassName)}
          scrollContainerClassName={scrollContainerClassName}
        >
          <div
            className={cn(
              'no-scrollbar flex gap-6 overflow-x-auto pl-4 md:pl-[36px] lg:pl-0',
              contentClassName,
            )}
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="shrink-0">
                <CreatorAvatarSkeleton />
              </div>
            ))}
          </div>
        </SwimlaneSkeleton>
      </div>
    )
  }

  if (!creators.length) {
    if (!emptyState) return null
    return (
      <section
        id="following-creators"
        data-section="profile-following-creators"
        className={cn('flex scroll-mt-40 flex-col gap-6 px-4 md:gap-9', sectionClassName)}
      >
        <div className="flex items-center justify-between">
          <h2 className="subheader-1 md:header-4 relative font-bold">
            {title ?? t('profile-page.following-creators.title')}
          </h2>
          {titleAction}
        </div>
        {emptyState}
      </section>
    )
  }

  return (
    <div id="following-creators" data-section="profile-following-creators" className="scroll-mt-40">
      <Swimlane
        title={title ?? t('profile-page.following-creators.title')}
        titleAction={titleAction}
        className={cn('gap-4 md:gap-2 lg:gap-[10px]', sectionClassName)}
        titleClassName="md:text-[24px]"
        scrollContainerClassName={scrollContainerClassName}
        renderContent={(scrollRef) => (
          <CreatorScrollContainer
            scrollRef={scrollRef}
            creators={creators}
            contentClassName={contentClassName}
            loadMoreRef={loadMoreRef}
            enableLoadMore={enableLoadMore}
            isValidating={isValidating}
          />
        )}
      />
    </div>
  )
}

function CreatorScrollContainer({
  scrollRef,
  creators,
  contentClassName,
  loadMoreRef,
  enableLoadMore,
  isValidating,
}: {
  scrollRef: React.RefObject<HTMLDivElement | null>
  creators: FollowingCreator[]
  contentClassName?: string
  loadMoreRef: (node?: Element | null) => void
  enableLoadMore: boolean
  isValidating: boolean
}) {
  useDraggingScrollMotion(scrollRef)

  return (
    <div
      ref={scrollRef}
      className={cn(
        'no-scrollbar flex cursor-grab gap-6 overflow-x-auto overflow-y-hidden pl-4 select-none md:pl-[36px] lg:pl-0',
        contentClassName,
      )}
    >
      {creators.map((creator, index) => (
        <div
          key={creator.preferredUsername}
          className={
            !enableLoadMore && index === creators.length - 1
              ? 'shrink-0 md:mr-[36px] lg:mr-6'
              : 'shrink-0'
          }
        >
          <CreatorAvatar
            communityUserId={creator.preferredUsername}
            headIconUrl={creator.headIconUrl || ''}
            displayName={creator.nickName || creator.name || creator.preferredUsername}
            isVerified={hasVerifiedCreatorBadge(creator)}
            disableDrag
          />
        </div>
      ))}
      {isValidating &&
        Array.from({ length: 4 }).map((_, i) => (
          <div key={`skeleton-${i}`} className="shrink-0">
            <CreatorAvatarSkeleton />
          </div>
        ))}
      {enableLoadMore && <div ref={loadMoreRef} className="h-full w-4 shrink-0" />}
    </div>
  )
}
